import { SECTIONS } from '@/components/wizard/constants';
import type { ReportData, CustomReportSection } from '@/types/report';

const REPORT_BODY_SECTION_IDS = [
    SECTIONS.DEMAND_DESCRIPTION,
    SECTIONS.PROCEDURES,
    SECTIONS.BEHAVIOR_OBSERVATIONS,
    SECTIONS.CLINICAL_HISTORY,
    SECTIONS.TESTS,
    SECTIONS.DIAGNOSIS,
    SECTIONS.RECOMMENDATIONS,
] as const;

const DEFAULT_REPORT_SECTION_ORDER = [...REPORT_BODY_SECTION_IDS];

const REPORT_BODY_SECTION_ID_SET = new Set<string>(REPORT_BODY_SECTION_IDS);

export const isBuiltInReportSectionId = (sectionId?: string | null) =>
    Boolean(sectionId && REPORT_BODY_SECTION_ID_SET.has(sectionId));

export const createCustomSectionId = () => {
    const randomId = Math.random().toString(36).slice(2, 10);
    return `custom-${Date.now()}-${randomId}`;
};

const getCustomSections = (data?: Partial<ReportData> | null): CustomReportSection[] =>
    data?.custom_sections ?? [];

export const getCustomSectionById = (data: Partial<ReportData>, sectionId: string) =>
    getCustomSections(data).find((section) => section.id === sectionId);

export const hasCustomReportSections = (data?: Partial<ReportData> | null) => {
    if ((data?.custom_sections?.length ?? 0) > 0) return true;

    return data?.section_order?.some((sectionId) => !isBuiltInReportSectionId(sectionId)) ?? false;
};

export const resolveReportSectionOrder = (data?: Partial<ReportData> | null) => {
    const customSectionIds = getCustomSections(data)
        .map((section) => section.id)
        .filter(Boolean);
    const sourceOrder = Array.isArray(data?.section_order)
        ? data.section_order
        : DEFAULT_REPORT_SECTION_ORDER;
    const sourceOrderWithMissingCustomSections = [
        ...sourceOrder,
        ...customSectionIds.filter((sectionId) => !sourceOrder.includes(sectionId)),
    ];
    const customSectionIdSet = new Set(customSectionIds);
    const slots: string[][] = Array.from({ length: REPORT_BODY_SECTION_IDS.length + 1 }, () => []);
    const placedCustomSectionIds = new Set<string>();

    sourceOrderWithMissingCustomSections.forEach((sectionId, index) => {
        if (!customSectionIdSet.has(sectionId)) return;
        if (placedCustomSectionIds.has(sectionId)) return;

        const precedingBuiltInIndexes = sourceOrderWithMissingCustomSections
            .slice(0, index)
            .map((candidateId) =>
                REPORT_BODY_SECTION_IDS.indexOf(
                    candidateId as (typeof REPORT_BODY_SECTION_IDS)[number],
                ),
            )
            .filter((candidateIndex) => candidateIndex >= 0);
        const slotIndex =
            precedingBuiltInIndexes.length > 0 ? Math.max(...precedingBuiltInIndexes) + 1 : 0;

        slots[slotIndex].push(sectionId);
        placedCustomSectionIds.add(sectionId);
    });

    const orderedIds: string[] = [...slots[0]];
    REPORT_BODY_SECTION_IDS.forEach((sectionId, index) => {
        orderedIds.push(sectionId, ...slots[index + 1]);
    });

    return orderedIds;
};

export const getSectionContent = (data: Partial<ReportData>, sectionId: string) => {
    if (isBuiltInReportSectionId(sectionId)) {
        const sectionData = data[sectionId as keyof ReportData] as { content?: string } | undefined;
        return sectionData?.content;
    }

    return getCustomSectionById(data, sectionId)?.content;
};

export const getSectionTitle = (
    data: Partial<ReportData>,
    sectionId: string,
    fallbackTitle: string,
) => {
    if (isBuiltInReportSectionId(sectionId)) {
        const sectionData = data[sectionId as keyof ReportData] as
            | { section_title?: string }
            | undefined;
        return sectionData?.section_title || fallbackTitle;
    }

    return getCustomSectionById(data, sectionId)?.section_title || fallbackTitle;
};
